import * as React from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Button } from "@/components/ui/button";
import { getMyPartnerDashboard } from "@/lib/partners.functions";
import {
  MISSING_RESOURCE_COPY,
  PARTNER_RESOURCES,
} from "@/lib/partner-resources";

export const Route = createFileRoute("/_authed/partners/resources")({
  head: () => ({
    meta: [
      { title: "Partner resource hub: CapitalDart" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: PartnerResourcesPage,
});

function PartnerResourcesPage() {
  const load = useServerFn(getMyPartnerDashboard);
  const dash = useQuery({ queryKey: ["partner-dashboard"], queryFn: () => load() });

  if (dash.isLoading) {
    return <main className="mx-auto max-w-[900px] px-4 py-10 text-brand-muted">Loading…</main>;
  }
  if (dash.data?.partner?.status !== "approved") {
    return (
      <main className="mx-auto max-w-[900px] px-4 py-10">
        <p className="text-[15px] text-brand-muted">
          The resource hub is open to approved partners.{" "}
          <Link to="/partners/apply" className="underline">
            Apply to partner
          </Link>
          .
        </p>
      </main>
    );
  }

  const link = dash.data.referralLink;

  return (
    <main className="mx-auto max-w-[900px] px-4 pb-20 pt-6">
      <Link
        to="/partners/dashboard"
        className="text-[12.5px] text-brand-muted no-underline hover:text-brand-ink"
      >
        ← Back to your partner dashboard
      </Link>
      <h1 className="mt-2 font-serif text-[32px] font-normal leading-tight tracking-tight text-brand-ink">
        Resource hub.
      </h1>
      <p className="mt-1 max-w-[70ch] text-[14.5px] leading-relaxed text-brand-muted">
        Copy you can use as it is. Read the compliance one-pager first. Where a
        piece is still marked draft, the text is a placeholder and should not
        be sent to a client yet.
      </p>
      {link && (
        <p className="mt-2 text-[13px] text-brand-muted">
          Your link, to paste wherever a piece calls for it:{" "}
          <span className="font-mono text-brand-ink">{link}</span>
        </p>
      )}

      {MISSING_RESOURCE_COPY.length > 0 && (
        <div className="mt-6 rounded-xl border border-brand-line bg-brand-paper-2 p-4">
          <p className="text-[13px] font-semibold text-brand-ink">
            Still placeholder text ({MISSING_RESOURCE_COPY.length})
          </p>
          <p className="mt-1 text-[13px] text-brand-muted">
            {MISSING_RESOURCE_COPY.join(", ")}.
          </p>
        </div>
      )}

      <div className="mt-8 space-y-4">
        {PARTNER_RESOURCES.map((r) => (
          <ResourceCard key={r.id} id={r.id} />
        ))}
      </div>
    </main>
  );
}

function ResourceCard({ id }: { id: string }) {
  const resource = PARTNER_RESOURCES.find((r) => r.id === id)!;
  const [copied, setCopied] = React.useState(false);
  return (
    <section className="rounded-2xl border border-brand-line bg-brand-paper p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="font-serif text-[20px] text-brand-ink">{resource.title}</h2>
        <span
          className={`rounded-full px-2 py-[2px] text-[11px] uppercase tracking-wider ${
            resource.state === "final"
              ? "bg-brand-paper-2 text-brand-muted"
              : "bg-[#F6E9C9] text-[#7A5A12]"
          }`}
        >
          {resource.state === "final" ? "Ready to use" : "Draft copy"}
        </span>
      </div>
      <p className="mt-1 text-[13.5px] text-brand-muted">{resource.summary}</p>
      <pre className="mt-3 whitespace-pre-wrap rounded-lg border border-brand-line bg-white p-3 font-sans text-[13.5px] leading-relaxed text-brand-ink">
        {resource.body}
      </pre>
      <Button
        className="mt-3"
        variant="outline"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(resource.body);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          } catch {
            setCopied(false);
          }
        }}
      >
        {copied ? "Copied" : "Copy"}
      </Button>
    </section>
  );
}
